/**
 * The FM Manager's top bar pieces (FM Manager rebuild C0.2).
 *
 * FmSitePicker — "My sites (n)" or "All sites" (A11's wording), opening the
 * list of the caller's sites by full name; picking one narrows the FM home to
 * it. "All my sites" clears it. The choice is remembered on this device only
 * (useFmSite) and is never a scope: the server narrows to the caller's sites
 * whatever is picked.
 *
 * FmGreeting — "{Good morning/afternoon/evening}, {first} · {hh:mm} {TZ} ·
 * {weekday, d month}" in the I8 zone (fmZone: the picked site's, else the
 * default building's, else the first of the caller's sites with a zone), the
 * zone labelled as people read it ("IST", via fmZoneAbbr). Hidden on phones
 * (C0.2), where the picker stays.
 */
import { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '@/stores/authStore';
import { clockReading } from '@/utils/timezone';
import { orgFormat } from '@/utils/format';
import { fmZoneAbbr } from '@/utils/fmTime';
import { fmZone, selectedSite, useFmShell, useFmSite } from '@/hooks/useFmShell';

export function greetingFor(hour: number): string {
  return hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
}

/** The greeting line for an instant in a zone — pure, for the tests. */
export function fmGreetingLine(now: Date, first: string, timeZone: string, locale?: string): string {
  const hour = Number(new Intl.DateTimeFormat('en-GB', {
    hour: 'numeric', hourCycle: 'h23', timeZone }).format(now));
  const { time } = clockReading(now, timeZone);
  // A recognisable abbreviation — "IST" for Asia/Kolkata even when the
  // organisation's locale would print "GMT+5:30" (spec C0.2: "15:10 IST").
  const zone = fmZoneAbbr(timeZone, now, locale);
  const weekday = new Intl.DateTimeFormat('en-GB', { weekday: 'long', timeZone }).format(now);
  const dayMonth = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', timeZone }).format(now);
  const day = `${weekday}, ${dayMonth}`;
  return `${greetingFor(hour)}, ${first} · ${time} ${zone} · ${day}`;
}

export function FmGreeting() {
  const user = useAuthStore((s) => s.user);
  const { data } = useFmShell();
  const site = useFmSite((s) => s.site);
  const zone = fmZone(data, site);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);
  if (!user || !zone) return null;
  return (
    <span data-testid="fm-greeting" title={`Time at your site: ${zone}`}
          className="hidden truncate text-xs font-medium text-content-tertiary lg:inline">
      {fmGreetingLine(now, user.first_name, zone, orgFormat().locale)}
    </span>
  );
}

export function FmSitePicker() {
  const { data } = useFmShell();
  const site = useFmSite((s) => s.site);
  const setSite = useFmSite((s) => s.setSite);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const user = useAuthStore((s) => s.user);
  const n = (user?.site_ids ?? []).length;
  const scopeLabel = n === 0 ? 'All sites' : `My sites (${n})`;
  const picked = selectedSite(data, site);
  return (
    <div className="relative shrink-0" ref={ref}>
      <button type="button" data-testid="site-scope-chip" aria-haspopup="listbox" aria-expanded={open}
              onClick={() => setOpen((o) => !o)}
              title={n === 0 ? 'You can see every site' : 'Limited to your sites'}
              className={`inline-flex min-h-[32px] max-w-[11rem] items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                n === 0 ? 'bg-brand-50 text-brand-700' : 'bg-surface-muted text-content-secondary'}`}>
        <span className="truncate">{picked ? picked.name : scopeLabel}</span>
        <ChevronDownIcon className="h-3 w-3 shrink-0" aria-hidden="true" />
      </button>
      {open && (
        <ul role="listbox" aria-label="Choose a site"
            className="absolute right-0 z-50 mt-1 max-h-72 w-64 max-w-[calc(100vw-2rem)] overflow-y-auto rounded-lg border border-line bg-surface py-1 shadow-lg">
          <li role="option" aria-selected={!picked}>
            <button type="button" className="block w-full px-3 py-2 text-left text-sm hover:bg-surface-subtle"
                    onClick={() => { setSite(null); setOpen(false); }}>
              {scopeLabel === 'All sites' ? 'All sites' : `All my sites (${n})`}
            </button>
          </li>
          {(data?.sites ?? []).map((s) => (
            <li key={s.id} role="option" aria-selected={picked?.id === s.id}>
              <button type="button" className={`block w-full px-3 py-2 text-left text-sm hover:bg-surface-subtle ${
                picked?.id === s.id ? 'font-semibold text-brand-700' : ''}`}
                      onClick={() => { setSite(s.id); setOpen(false); }}>
                {s.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
